import {
  Button,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  useDialogController,
} from '@ff-it/ui';

import { ReactElement, useMemo, useState } from 'react';
import { ListCampaign } from 'modules/campaign/campaign/types';
import { IndexTable, selectionColumn, TableSelectionProvider } from 'components/ControlledTable';
import { columns as campaignListColumns } from 'routes/Agency/Planning/Campaigns/columns';
import { Filter } from 'modules/campaign/common/Filter';
import { CampaignChipTray } from './CampaignChipTray';
import { container, dialogContainer, tableContainer } from './style.css';
import { FilterType } from 'components/ControlledTable/types';

type CampaignSelectDialogProps = {
  value: ListCampaign[];
  fixedIds?: number[];
  defaultFilter?: FilterType;
  onChange: (value: ListCampaign[]) => void;
};

const columns = [selectionColumn, ...campaignListColumns];
const tableProps = {
  containerClassName: container,
  tableContainerClassName: tableContainer,
  size: 'xs',
  sticky: true,
} as const;

export function CampaignSelectDialog({
  value: currentValue,
  fixedIds,
  defaultFilter,
  onChange,
}: CampaignSelectDialogProps): ReactElement {
  const dialog = useDialogController();
  const [value, setValue] = useState(currentValue);
  const selectedIds = useMemo(() => new Set(value.map((c) => c.id)), [value]);
  const fixed = useMemo(() => new Set(fixedIds), [fixedIds]);
  const getRowSelection = (row: ListCampaign) => {
    const { id } = row;
    return {
      selected: selectedIds.has(id),
      disabled: fixed.has(id),
      onChange: (selected: boolean) =>
        setValue((current) => (selected ? [...current, row] : current.filter((c) => c.id !== id))),
    };
  };
  const onSelect = () => {
    onChange(value);
    dialog.setOpen(false);
  };
  return (
    <DialogContent className={dialogContainer} testId="source-campaigns-dialog">
      <TableSelectionProvider value={getRowSelection}>
        <DialogHeader title="Select campaign" mb="-2" />
        <DialogBody display="flex" flexDirection="column" flexGrow={1} minHeight="0">
          <IndexTable<any>
            url="campaign/campaigns/"
            filterFields={<Filter scope="campaign" />}
            columns={columns}
            urlSync={null}
            storageKey={null}
            tableProps={tableProps}
            defaultFilter={defaultFilter}
          />
        </DialogBody>
        <DialogFooter gap="3" alignItems="flex-start" mt="-1">
          <DialogClose />
          <CampaignChipTray
            values={value}
            fixedIds={fixedIds}
            onRemove={(id: number) => setValue((current) => current.filter((c) => c.id !== id))}
          />

          <Button ml="auto" onClick={onSelect}>
            Select
          </Button>
        </DialogFooter>
      </TableSelectionProvider>
    </DialogContent>
  );
}
